import type { StockAdminQueue } from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { meQuery } from '../../api';
import { queueQuery } from '../../api/ops';
import { ButtonLink, Card, plural } from '../../ui';
import { QUEUE_VIEW } from '../labels';
import { PersonChip } from '../shared';
import { whoIsWorking } from './presenter';
import s from '../Ops.module.css';

/**
 * "Who's working the queue" (SA1): everyone on the Stock Admin team and what each holds now. It
 * names colleagues to colleagues, so it's only ever drawn on the Stock Admin team's own pages,
 * from their own reads; a preview draws the page's layout, never this list.
 */
export function WhoIsWorkingCard({
  teammates,
  showInProgressLink = true,
}: {
  teammates: StockAdminQueue['teammates'];
  showInProgressLink?: boolean;
}): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  // Every claim is in "Everything in progress": who's working reads it (shared cache).
  const { data: inProgress } = useQuery(queueQuery('in_progress'));
  const working = whoIsWorking(
    { id: me.person.id, name: me.person.displayName, initials: me.person.initials },
    inProgress,
    teammates,
  );
  return (
    <Card
      title="Who’s working the queue"
      tone="dark"
      compact
      titleSize="sm"
      help={{
        shows: 'Everyone on the Stock Admin team and the awards each one holds now.',
        canDo: 'A claim shows everyone who’s on an award, so nobody checks the same award twice.',
      }}
    >
      <ul className={s.teamList}>
        {working.map((w) => (
          <li key={w.person.id}>
            <PersonChip
              initials={w.person.initials}
              name={w.you ? `${w.person.name} · you` : w.person.name}
              detail={
                w.holds.length === 0
                  ? 'Nothing claimed'
                  : `${plural(w.holds.length, 'claim', 'claims')} · ${w.holds.slice(0, 3).join(', ')}${w.holds.length > 3 ? ' …' : ''}`
              }
              tone={w.you ? 'cyan' : 'sky'}
            />
          </li>
        ))}
      </ul>
      <p className={s.onDarkNote}>
        A claim shows everyone who is on a request, so nobody checks the same award twice.
      </p>
      {showInProgressLink && (
        <ButtonLink href={QUEUE_VIEW.in_progress.href} variant="ghost-on-dark" size="sm">
          Everything in progress
        </ButtonLink>
      )}
    </Card>
  );
}
